import React, { useCallback, useEffect, useRef, useState } from "react";
import { tasksAPI } from "../../api/client";
import useAuth from "../../hooks/useAuth";
import CommentComposer from "./CommentComposer";
import TaskChat from "./TaskChat";
import TimeLogSection from "./TimeLogSection";
import AttachmentSection from "./AttachmentSection";
import RepeatField from "./RepeatField";
import useConfirm from "./ConfirmDialog";
import {
  apiError,
  dueState,
  formatDueDate,
  formatMinutes,
  formatTimestamp,
  initials,
  APPROVAL_LABELS,
  TASK_TYPE_LABELS,
} from "./taskUtils";
import { PERMISSIONS } from "../../constants/permissions";
import { usePermission } from "../../hooks/usePermission";

export function CheckCircle({ checked, onToggle, label, size = "md" }) {
  return (
    <button
      type="button"
      className={`tk-check tk-check-${size} ${checked ? "checked" : ""}`.trim()}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      aria-pressed={checked}
      aria-label={label || (checked ? "Mark incomplete" : "Mark complete")}
      title={checked ? "Mark incomplete" : "Mark complete"}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 12.5l3.2 3.2L17 9" />
      </svg>
    </button>
  );
}

const APPROVAL_ICON_PATHS = {
  pending: "M12 7v5l3 2M21 12a9 9 0 1 1-9-9 9 9 0 0 1 9 9z",
  approved: "M7 12.5l3.2 3.2L17 9",
  changes_requested: "M12 8v5M12 16.5v.01",
  rejected: "M8 8l8 8M16 8l-8 8",
};

// The completion control for any task: a check circle for tasks, a diamond
// for milestones (both toggle), and a read-only decision badge for approvals,
// which are closed by approving or rejecting in the task pane.
export function TaskStatusIcon({ task, onToggle, size = "md" }) {
  if (task.type === "approval") {
    const status = task.approvalStatus || "pending";
    return (
      <span
        className={`tk-approval-icon tk-check-${size} ${status}`}
        title={APPROVAL_LABELS[status]}
        role="img"
        aria-label={APPROVAL_LABELS[status]}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d={APPROVAL_ICON_PATHS[status]} />
        </svg>
      </span>
    );
  }
  if (task.type === "milestone") {
    return (
      <button
        type="button"
        className={`tk-milestone-icon tk-check-${size} ${task.completed ? "checked" : ""}`.trim()}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-pressed={task.completed}
        aria-label={
          task.completed
            ? "Mark milestone incomplete"
            : "Mark milestone complete"
        }
        title={task.completed ? "Milestone reached" : "Mark milestone complete"}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2.5l9.5 9.5-9.5 9.5L2.5 12z" />
        </svg>
      </button>
    );
  }
  return (
    <CheckCircle checked={task.completed} onToggle={onToggle} size={size} />
  );
}

function AutoGrowTextarea({ value, onChange, onCommit, className, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      className={className}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      {...rest}
    />
  );
}

function DependencyPicker({ taskId, existingIds, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(() => {
      tasksAPI
        .search(q, taskId)
        .then((resp) => setResults(resp.data || []))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(handle);
  }, [open, q, taskId]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const options = results.filter((r) => !existingIds.includes(r.id));

  return (
    <div className="tk-picker" ref={wrapRef}>
      {!open ? (
        <button
          type="button"
          className="tk-link-btn"
          onClick={() => setOpen(true)}
        >
          + Add dependency
        </button>
      ) : (
        <>
          <input
            autoFocus
            className="tk-input"
            placeholder="Search tasks…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
          />
          <ul className="tk-picker-menu">
            {options.length === 0 && (
              <li className="tk-picker-empty">No matching tasks</li>
            )}
            {options.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  onClick={() => {
                    onPick(option.id);
                    setOpen(false);
                    setQ("");
                  }}
                >
                  <span className={option.completed ? "tk-done-text" : ""}>
                    {option.name}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

const DECISIONS = [
  { value: "approved", label: "Approve", className: "approve" },
  {
    value: "changes_requested",
    label: "Request changes",
    className: "changes",
  },
  { value: "rejected", label: "Reject", className: "reject" },
];

// Approval toolbar: the approver decides; everyone else sees who it's with.
function ApprovalControls({ task, onDecide }) {
  const status = task.approvalStatus || "pending";
  if (!task.canDecide) {
    return (
      <span className={`tk-approval-pill ${status}`}>
        {status === "pending"
          ? `Waiting on ${task.assignee?.name || "an approver"}`
          : APPROVAL_LABELS[status]}
      </span>
    );
  }
  return (
    <div
      className="tk-approval-actions"
      role="group"
      aria-label="Approval decision"
    >
      {DECISIONS.map((d) => (
        <button
          key={d.value}
          type="button"
          className={`tk-decide-btn ${d.className} ${status === d.value ? "active" : ""}`.trim()}
          aria-pressed={status === d.value}
          onClick={() => onDecide(status === d.value ? "pending" : d.value)}
          title={status === d.value ? "Click again to undo" : d.label}
        >
          {d.label}
        </button>
      ))}
    </div>
  );
}

export default function TaskDetailPane({
  taskId,
  people,
  projectOptions,
  onClose,
  onOpenTask,
  onChanged,
  onDeleted,
}) {
  const { user } = useAuth();
  const canCreate = usePermission(PERMISSIONS.TASK_CREATE);
  // Without task:assign the picker offers only yourself (and whoever already has it).
  const canAssign = usePermission(PERMISSIONS.TASK_ASSIGN);
  const [task, setTask] = useState(null);
  const [error, setError] = useState("");
  const [confirm, confirmDialog] = useConfirm();
  const [nameDraft, setNameDraft] = useState("");
  const [descDraft, setDescDraft] = useState("");
  const [feedFilter, setFeedFilter] = useState("all");
  const [subtaskDraft, setSubtaskDraft] = useState(null);
  const creatingSubtask = useRef(false);
  const feedEndRef = useRef(null);

  const applyTask = useCallback((next) => {
    setTask(next);
    setNameDraft(next.name);
    setDescDraft(next.description || "");
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTask(null);
    setError("");
    setSubtaskDraft(null);
    tasksAPI
      .get(taskId)
      .then((resp) => !cancelled && applyTask(resp.data))
      .catch(
        (err) => !cancelled && setError(apiError(err, "Could not load task")),
      );
    return () => {
      cancelled = true;
    };
  }, [taskId, applyTask]);

  // Mutations on this task return it in full, so the pane refreshes from the
  // response; mutations on a subtask return the subtask, so reload instead.
  const run = async (request, { reload = false } = {}) => {
    setError("");
    try {
      const resp = await request();
      if (reload) applyTask((await tasksAPI.get(taskId)).data);
      else if (resp?.data) applyTask(resp.data);
      onChanged?.();
      return true;
    } catch (err) {
      setError(apiError(err));
      return false;
    }
  };

  const update = (patch) => run(() => tasksAPI.update(taskId, patch));

  if (!task) {
    return (
      <aside className="tk-pane">
        <div className="tk-pane-toolbar">
          <span />
          <button
            type="button"
            className="tk-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="tk-pane-body">
          {error ? (
            <p className="tk-error">{error}</p>
          ) : (
            <p className="tk-muted">Loading…</p>
          )}
        </div>
      </aside>
    );
  }

  const linkedProjectIds = task.projects.map((p) => p.id);
  const availableProjects = projectOptions.filter(
    (p) => !linkedProjectIds.includes(p.id),
  );
  const waitingOn = task.dependencies.filter((d) => !d.completed);
  const due = dueState(task.dueDate, task.completed);

  const deleteTask = async () => {
    const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
    // Spell out what goes with the task so nobody deletes history by accident.
    const consequences = [
      task.subtasks.length &&
        `${plural(task.subtasks.length, "subtask")} will be deleted too`,
      task.comments.length &&
        `${plural(task.comments.length, "message")} in the discussion`,
      task.minutesLogged > 0 &&
        `${formatMinutes(task.minutesLogged)} of logged time — it will drop out of reports`,
      task.dependents.length &&
        `${plural(task.dependents.length, "task")} waiting on this will no longer be blocked`,
    ].filter(Boolean);
    const ok = await confirm({
      title: `Delete this ${task.type === "task" ? "task" : task.type}?`,
      subject: task.name,
      message: consequences.length
        ? "This also removes:"
        : "This can't be undone.",
      consequences: consequences.length
        ? [...consequences, "This can't be undone."]
        : [],
      confirmLabel: `Delete ${task.type === "task" ? "task" : task.type}`,
    });
    if (!ok) return;
    try {
      await tasksAPI.remove(taskId);
      onDeleted?.(task);
    } catch (err) {
      setError(apiError(err));
    }
  };

  const createSubtask = async () => {
    const name = subtaskDraft?.trim();
    if (!name) {
      setSubtaskDraft(null);
      return;
    }
    if (creatingSubtask.current) return;
    creatingSubtask.current = true;
    setSubtaskDraft("");
    const ok = await run(
      () => tasksAPI.create({ name, parentTaskId: taskId }),
      {
        reload: true,
      },
    );
    creatingSubtask.current = false;
    if (!ok) setSubtaskDraft(name);
  };

  const toggleSubtask = (subtask) =>
    run(() => tasksAPI.update(subtask.id, { completed: !subtask.completed }), {
      reload: true,
    });

  const assignablePeople = canAssign
    ? people
    : people.filter((p) => p.id === user?.id || p.id === task.assignee?.id);

  return (
    <aside className="tk-pane" aria-label="Task details">
      <div className="tk-pane-toolbar">
        {task.type === "approval" ? (
          <ApprovalControls
            task={task}
            onDecide={(decision) =>
              run(() => tasksAPI.decide(taskId, decision))
            }
          />
        ) : (
          <button
            type="button"
            className={`tk-complete-btn ${task.completed ? "done" : ""}`.trim()}
            onClick={() => update({ completed: !task.completed })}
            aria-pressed={task.completed}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d={
                  task.type === "milestone"
                    ? "M12 3.5l8.5 8.5-8.5 8.5L3.5 12z"
                    : "M7 12.5l3.2 3.2L17 9"
                }
              />
            </svg>
            {task.completed
              ? task.type === "milestone"
                ? "Milestone reached"
                : "Completed"
              : task.type === "milestone"
                ? "Mark milestone complete"
                : "Mark complete"}
          </button>
        )}
        <div className="tk-pane-toolbar-right">
          {task.canDelete && (
            <button
              type="button"
              className="tk-icon-btn"
              onClick={deleteTask}
              aria-label="Delete task"
              title="Delete task"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
              </svg>
            </button>
          )}
          <button
            type="button"
            className="tk-icon-btn"
            onClick={onClose}
            aria-label="Close task"
            title="Close"
          >
            ×
          </button>
        </div>
      </div>

      <div className="tk-pane-body">
        {task.type === "approval" && task.approvalDecidedBy && (
          <div className={`tk-banner-approval ${task.approvalStatus}`}>
            {APPROVAL_LABELS[task.approvalStatus]} by{" "}
            {task.approvalDecidedBy.name} ·{" "}
            {formatTimestamp(task.approvalDecidedAt)}
          </div>
        )}
        {task.type !== "approval" && task.completed && task.completedBy && (
          <div className="tk-banner-done">
            Completed by {task.completedBy.name} ·{" "}
            {formatTimestamp(task.completedAt)}
          </div>
        )}
        {waitingOn.length > 0 && !task.completed && (
          <div className="tk-banner-waiting">
            Waiting on {waitingOn.length} task{waitingOn.length > 1 ? "s" : ""}{" "}
            to be completed
          </div>
        )}
        {task.parentTask && (
          <button
            type="button"
            className="tk-parent-link"
            onClick={() => onOpenTask(task.parentTask.id)}
          >
            ↑ {task.parentTask.name}
          </button>
        )}

        <AutoGrowTextarea
          className={`tk-title-input ${task.completed ? "tk-done-text" : ""}`.trim()}
          value={nameDraft}
          onChange={setNameDraft}
          onCommit={() => {
            if (!nameDraft.trim()) setNameDraft(task.name);
            else if (nameDraft.trim() !== task.name)
              update({ name: nameDraft });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          aria-label="Task name"
        />

        {error && <p className="tk-error">{error}</p>}

        <div className="tk-fields">
          <label className="tk-field-label" htmlFor="tk-type">
            Type
          </label>
          <div className="tk-field-value">
            <select
              id="tk-type"
              className="tk-input"
              value={task.type}
              onChange={(e) => update({ type: e.target.value })}
            >
              {Object.entries(TASK_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <label className="tk-field-label" htmlFor="tk-assignee">
            {task.type === "approval" ? "Approver" : "Assignee"}
          </label>
          <div className="tk-field-value">
            <select
              id="tk-assignee"
              className="tk-input"
              value={task.assignee?.id || ""}
              onChange={(e) => update({ assigneeId: e.target.value || null })}
            >
              <option value="">No assignee</option>
              {assignablePeople.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.id === user?.id ? " (me)" : ""}
                </option>
              ))}
            </select>
          </div>

          {/* A repeating task's date comes from its pattern and "Starting
              from" day, so the Repeat field below replaces this row. */}
          {!task.recurrence && (
            <>
              <label className="tk-field-label" htmlFor="tk-due">
                Due date
              </label>
              <div className="tk-field-value">
                <input
                  id="tk-due"
                  type="date"
                  className={`tk-input tk-due-input ${due ? `due-${due}` : ""}`.trim()}
                  value={task.dueDate || ""}
                  onChange={(e) => update({ dueDate: e.target.value || null })}
                />
                {task.dueDate && (
                  <button
                    type="button"
                    className="tk-link-btn"
                    onClick={() => update({ dueDate: null })}
                  >
                    Clear
                  </button>
                )}
              </div>
            </>
          )}

          {!task.parentTask && (
            <>
              <label className="tk-field-label" htmlFor="tk-repeat">
                Repeat
              </label>
              <div className="tk-field-value">
                <RepeatField
                  task={task}
                  onSave={update}
                  onOpenTask={onOpenTask}
                />
              </div>
            </>
          )}

          <span className="tk-field-label">Projects</span>
          <div className="tk-field-value tk-chip-row">
            {task.projects.map((p) => (
              <span key={p.id} className="tk-project-link">
                <span className="tk-chip">
                  {p.name}
                  <button
                    type="button"
                    onClick={() =>
                      run(() => tasksAPI.removeProject(taskId, p.id))
                    }
                    aria-label={`Remove from ${p.name}`}
                  >
                    ×
                  </button>
                </span>
                {p.sections?.length > 0 && (
                  <select
                    className="tk-input tk-section-select"
                    value={p.sectionId || ""}
                    onChange={(e) =>
                      run(() =>
                        tasksAPI.moveToSection(
                          taskId,
                          p.id,
                          e.target.value || null,
                        ),
                      )
                    }
                    aria-label={`Section in ${p.name}`}
                    title="Section in this project"
                  >
                    <option value="">No section</option>
                    {p.sections.map((sec) => (
                      <option key={sec.id} value={sec.id}>
                        {sec.name}
                      </option>
                    ))}
                  </select>
                )}
              </span>
            ))}
            {availableProjects.length > 0 && (
              <select
                className="tk-input tk-inline-select"
                value=""
                onChange={(e) =>
                  e.target.value &&
                  run(() => tasksAPI.addProject(taskId, e.target.value))
                }
                aria-label="Add to project"
              >
                <option value="">+ Add to project</option>
                {availableProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <span className="tk-field-label">Dependencies</span>
          <div className="tk-field-value tk-dep-col">
            {task.dependencies.length > 0 && (
              <div className="tk-dep-group">
                <span className="tk-dep-caption">Waiting on</span>
                {task.dependencies.map((d) => (
                  <div key={d.id} className="tk-dep-row">
                    <span
                      className={`tk-dep-dot ${d.completed ? "done" : ""}`}
                    />
                    <button
                      type="button"
                      className={`tk-dep-name ${d.completed ? "tk-done-text" : ""}`.trim()}
                      onClick={() => onOpenTask(d.id)}
                    >
                      {d.name}
                    </button>
                    <button
                      type="button"
                      className="tk-icon-btn tk-icon-btn-sm"
                      onClick={() =>
                        run(() => tasksAPI.removeDependency(taskId, d.id))
                      }
                      aria-label={`Remove dependency on ${d.name}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {task.dependents.length > 0 && (
              <div className="tk-dep-group">
                <span className="tk-dep-caption">Blocking</span>
                {task.dependents.map((d) => (
                  <div key={d.id} className="tk-dep-row">
                    <span
                      className={`tk-dep-dot ${d.completed ? "done" : ""}`}
                    />
                    <button
                      type="button"
                      className={`tk-dep-name ${d.completed ? "tk-done-text" : ""}`.trim()}
                      onClick={() => onOpenTask(d.id)}
                    >
                      {d.name}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <DependencyPicker
              taskId={taskId}
              existingIds={task.dependencies.map((d) => d.id)}
              onPick={(id) => run(() => tasksAPI.addDependency(taskId, id))}
            />
          </div>
        </div>

        <div className="tk-section">
          <h3 className="tk-section-title">Description</h3>
          <AutoGrowTextarea
            className="tk-description"
            value={descDraft}
            onChange={setDescDraft}
            onCommit={() => {
              if (descDraft !== (task.description || "")) {
                update({ description: descDraft });
              }
            }}
            placeholder="What is this task about?"
            aria-label="Description"
          />
        </div>

        <div className="tk-section">
          <h3 className="tk-section-title">
            Subtasks
            {task.subtasks.length > 0 && (
              <span className="tk-count">
                {task.subtasks.filter((s) => s.completed).length}/
                {task.subtasks.length}
              </span>
            )}
          </h3>
          <div className="tk-subtasks">
            {task.subtasks.map((s) => {
              const subDue = dueState(s.dueDate, s.completed);
              return (
                <div
                  key={s.id}
                  className="tk-subtask-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpenTask(s.id)}
                  onKeyDown={(e) => e.key === "Enter" && onOpenTask(s.id)}
                >
                  <TaskStatusIcon
                    size="sm"
                    task={s}
                    onToggle={() => toggleSubtask(s)}
                  />
                  <span
                    className={`tk-subtask-name ${s.completed ? "tk-done-text" : ""}`}
                  >
                    {s.name}
                  </span>
                  {s.dueDate && (
                    <span className={`tk-due ${subDue ? `due-${subDue}` : ""}`}>
                      {formatDueDate(s.dueDate)}
                    </span>
                  )}
                  {s.assignee && (
                    <span
                      className="tk-avatar tk-avatar-sm"
                      title={s.assignee.name}
                    >
                      {initials(s.assignee.name)}
                    </span>
                  )}
                </div>
              );
            })}
            {subtaskDraft !== null ? (
              <div className="tk-subtask-row tk-subtask-new">
                <CheckCircle size="sm" checked={false} onToggle={() => {}} />
                <input
                  autoFocus
                  className="tk-subtask-input"
                  placeholder="Subtask name"
                  value={subtaskDraft}
                  onChange={(e) => setSubtaskDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.nativeEvent.isComposing) return;
                    if (e.key === "Enter") createSubtask();
                    if (e.key === "Escape") setSubtaskDraft(null);
                  }}
                  onBlur={() => !subtaskDraft.trim() && setSubtaskDraft(null)}
                />
              </div>
            ) : (
              canCreate && (
                <button
                  type="button"
                  className="tk-link-btn"
                  onClick={() => setSubtaskDraft("")}
                >
                  + Add subtask
                </button>
              )
            )}
          </div>
        </div>

        <TimeLogSection
          task={task}
          onLog={(data) => run(() => tasksAPI.logTime(taskId, data))}
          onRemove={(logId) => run(() => tasksAPI.removeTimeLog(taskId, logId))}
        />

        <AttachmentSection
          task={task}
          onUpload={(file) => run(() => tasksAPI.addAttachment(taskId, file))}
          onRemove={(attachmentId) =>
            run(() => tasksAPI.removeAttachment(taskId, attachmentId))
          }
        />

        <div className="tk-section tk-feed-section">
          <div className="tk-chat-header">
            <h3 className="tk-section-title">
              Discussion
              {task.comments.length > 0 && (
                <span className="tk-count">{task.comments.length}</span>
              )}
            </h3>
            <label className="tk-chat-toggle">
              <input
                type="checkbox"
                checked={feedFilter === "all"}
                onChange={(e) =>
                  setFeedFilter(e.target.checked ? "all" : "comments")
                }
              />
              Show activity
            </label>
          </div>
          <TaskChat
            comments={task.comments}
            activities={task.activities}
            showActivity={feedFilter === "all"}
            currentUserId={user?.id}
            canDelete={(c) => c.author?.id === user?.id || user?.isAdmin}
            onDelete={async (c) => {
              const ok = await confirm({
                title: "Delete this message?",
                subject: c.body.replace(/@\[([^\]]+)\]\([^)]+\)/g, "@$1"),
                message: "It will be removed from the discussion for everyone.",
                confirmLabel: "Delete message",
              });
              if (ok) run(() => tasksAPI.removeComment(taskId, c.id));
            }}
          />
          <div ref={feedEndRef} />
        </div>
      </div>

      <div className="tk-pane-footer">
        <CommentComposer
          people={people}
          onSubmit={async (body) => {
            const ok = await run(() => tasksAPI.addComment(taskId, body));
            if (!ok) throw new Error("comment failed");
            requestAnimationFrame(() =>
              feedEndRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "end",
              }),
            );
          }}
        />
        <div className="tk-collaborators">
          <span className="tk-muted">Collaborators</span>
          <div className="tk-avatar-stack">
            {[task.createdBy, ...task.collaborators]
              .filter(
                (p, idx, arr) =>
                  p && arr.findIndex((x) => x?.id === p.id) === idx,
              )
              .map((p) => (
                <span
                  key={p.id}
                  className="tk-avatar tk-avatar-sm"
                  title={p.name}
                >
                  {initials(p.name)}
                </span>
              ))}
          </div>
        </div>
      </div>
      {confirmDialog}
    </aside>
  );
}
